import { useNavigate } from 'react-router-dom';
import { ArrowRight, Zap, Users, FileCheck } from 'lucide-react';

export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#0d0e12] text-white">

      {/* Hero */}
      <section className="max-w-3xl mx-auto px-6 pt-20 pb-16 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#ff5a00]/10 border border-[#ff5a00]/20 text-[#ff5a00] text-xs font-bold tracking-widest uppercase mb-8">
          Notre histoire
        </div>
        <h1 className="text-4xl md:text-6xl font-black tracking-tight leading-tight mb-6">
          On a vécu le problème.<br />
          <span className="text-[#ff5a00]">On a construit la solution.</span>
        </h1>
        <p className="text-gray-400 text-lg leading-relaxed max-w-xl mx-auto">
          Organiser un festival, c'est jongler avec des dizaines de stands par email, tableurs et appels téléphoniques.
          Côté exposant, c'est candidater dans le vide sans jamais avoir de retour.
          Festin automatise tout ça.
        </p>
      </section>

      {/* Mission */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <div className="bg-white/3 border border-white/8 rounded-2xl p-8 md:p-12 text-center">
          <p className="text-xs font-black uppercase tracking-widest text-[#ff5a00] mb-4">Notre mission</p>
          <p className="text-2xl md:text-3xl font-bold text-white leading-snug">
            Connecter organisateurs de festivals et exposants — sans friction, sans email, sans tableur.
          </p>
        </div>
      </section>

      {/* Ce qu'on change */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <h2 className="text-xl font-black text-white mb-6 text-center">Ce qu'on change concrètement</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              icon: Users,
              title: 'Mise en relation intelligente',
              desc: 'Un algorithme de matching calcule la compatibilité entre chaque stand et chaque festival. Fini les candidatures à l\'aveugle.',
            },
            {
              icon: FileCheck,
              title: 'Contrat automatique',
              desc: 'Après confirmation du paiement, un contrat PDF est généré et envoyé instantanément aux deux parties.',
            },
            {
              icon: Zap,
              title: 'Tout en un seul endroit',
              desc: 'Candidature, acceptation, paiement, messagerie, contrat — un seul workflow de bout en bout.',
            },
          ].map(({ icon: Icon, title, desc }) => (
            <div key={title} className="bg-white/3 border border-white/8 rounded-xl p-6">
              <div className="w-10 h-10 rounded-xl bg-[#ff5a00]/10 flex items-center justify-center mb-4">
                <Icon className="w-5 h-5 text-[#ff5a00]" />
              </div>
              <h3 className="font-bold text-white mb-2">{title}</h3>
              <p className="text-sm text-gray-400 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fondateurs */}
      <section className="max-w-3xl mx-auto px-6 pb-16">
        <h2 className="text-xl font-black text-white mb-8 text-center">L'équipe</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {[
            {
              initials: 'M',
              name: 'Maxime',
              role: 'Co-fondateur',
              bio: 'À l\'origine du projet, Maxime a identifié le problème sur le terrain et construit la première version de Festin.',
            },
            {
              initials: 'A',
              name: 'Arié',
              role: 'Co-fondateur',
              bio: 'Arié apporte sa vision business et son réseau pour accélérer le développement commercial de Festin.',
            },
          ].map(({ initials, name, role, bio }) => (
            <div key={name} className="bg-white/3 border border-white/8 rounded-2xl p-6 flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-[#ff5a00] flex items-center justify-center text-white text-2xl font-black shrink-0">
                  {initials}
                </div>
                <div>
                  <p className="font-black text-white text-lg">{name}</p>
                  <p className="text-xs text-[#ff5a00] font-bold uppercase tracking-widest">{role}</p>
                </div>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">{bio}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-24 text-center">
        <div className="bg-gradient-to-br from-[#ff5a00]/10 to-transparent border border-[#ff5a00]/20 rounded-2xl p-10">
          <h2 className="text-2xl font-black text-white mb-3">Rejoignez Festin</h2>
          <p className="text-gray-400 mb-8 text-sm">
            Que vous soyez organisateur ou exposant, la plateforme est gratuite à l'inscription.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <button
              onClick={() => navigate('/')}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-[#ff5a00] hover:bg-[#e04e00] text-white font-bold rounded-xl transition-colors"
            >
              Commencer gratuitement <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => navigate('/contact')}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-white/5 hover:bg-white/10 text-white font-bold rounded-xl border border-white/10 transition-colors"
            >
              Nous contacter
            </button>
          </div>
        </div>
      </section>

    </div>
  );
}
